// Universal loader — the single tag a customer pastes in <head>:
//
//   <script async src="https://cdn.hotmaps.doogree.co.il/d.js" data-site="…"></script>
//
// It is product-agnostic: it knows only the manifest contract + `when` timing +
// per-module path scoping. The SAME source is meant to ship from every product
// CDN (HotMaps now, NAGISOT in Phase 3), so `d.js` is byte-identical per CDN —
// only the build-time __MANIFEST_ORIGIN__ define differs. See
// docs/UNIFIED_LOADER.md.
//
// On boot:
//   1. Read data-site off its own <script> tag.
//   2. Apply the cached manifest from localStorage immediately (zero network),
//      then revalidate from GET /m/:siteId and reconcile if it changed.
//   3. For each enabled module decide if it runs on THIS path (cfg.match), then
//      inject at its cheapest moment:
//        when:"head" → now (we're already in <head> → early enough for tracker)
//        when:"idle" → requestIdleCallback → end of <body> (NAGISOT widget)
//   4. Re-evaluate on SPA navigation (pushState/replaceState/popstate) so a
//      module appears/disappears as the visitor moves between, e.g., public and
//      internal-admin areas — HotMaps everywhere, accessibility only in public.
//   5. Live reconcile with no extra request: the tracker reads x-hm-rev off its
//      /e responses and dispatches `hm:rev`; we refetch only when rev changed.
//
// Disabled or non-matching modules cost nothing — they're never injected.

declare const __MANIFEST_ORIGIN__: string;
declare const __LOADER_VERSION__: string;

interface MatchRule {
  include?: string[]; // glob paths; if present, at least one must match
  exclude?: string[]; // glob paths; any match → skip (wins over include)
}
interface ModuleConfig {
  match?: MatchRule;
  hostSelectors?: string[]; // DOM nodes to remove when the module is unmounted
  // When true, the module is suppressed on a "secured" screen — one that
  // shows a password field (login / checkout / account auth). The
  // accessibility widget defaults to skipOnAuth:true (no FAB over a login
  // box); HotMaps can opt in too when the owner doesn't want recording on
  // auth pages. Re-evaluated on SPA nav AND on DOM mutation, so a login
  // form appearing/closing in an SPA flips the module live.
  skipOnAuth?: boolean;
  [k: string]: unknown;
}
interface ModuleEntry {
  k: string;                 // module key
  when: "head" | "idle";     // timing
  s: string;                 // script URL to inject
  d?: string;                // module's own data-site id
  a?: string;                // module's API origin
  cfg?: ModuleConfig;
}
interface Manifest {
  rev: number;
  mods: ModuleEntry[];
}

(function bootUnifiedLoader() {
  const W = window as unknown as { __hmLoader?: boolean };
  if (W.__hmLoader) return; // never run twice on one page
  W.__hmLoader = true;

  const selfEl = document.currentScript as HTMLScriptElement | null;
  const siteId = resolveSiteId(selfEl);
  // Identity: an explicit data-site wins; otherwise resolve by hostname. The
  // host path lets a multi-tenant host (e.g. PARSI) paste ONE identical tag with
  // no id — the server maps host→site (and self-provisions on first hit).
  const host = location.hostname;
  const ident: { kind: "id" | "host"; val: string } | null = siteId
    ? { kind: "id", val: siteId }
    : host
      ? { kind: "host", val: host }
      : null;
  if (!ident) {
    console.warn("[hm-loader] no data-site and no resolvable hostname");
    return;
  }
  const manifestOrigin = (selfEl?.dataset.manifest || __MANIFEST_ORIGIN__).replace(/\/$/, "");
  // data-products lets the employee pick what gets provisioned on first hit:
  //   (absent / "all")   → HotMaps + NAGISOT  (default)
  //   "hotmaps"          → HotMaps only (no accessibility widget)
  // Only affects the FIRST provisioning of an unknown host; afterwards the
  // dashboard module toggles are the source of truth.
  const products = (selfEl?.dataset.products || "").toLowerCase().trim();
  const LS_KEY = "hm_mods:" + ident.val;

  const injected = new Set<string>();   // modules actually mounted now
  const pendingIdle = new Set<string>(); // idle-scheduled, not yet mounted
  let current: Manifest | null = null;
  let appliedRev = -1;
  // Secured-screen memo — MUST be declared here, above the boot sequence
  // below. The offline-first `applyManifest(cached)` on step 1 runs
  // synchronously and reaches isSecuredScreen() → securedCache during this
  // same top-level execution. If this `let` lived further down (next to its
  // helpers) it would still be in its temporal dead zone at that point, and a
  // cached manifest would make every repeat page-load throw an (uncaught,
  // cross-origin-masked "Script error.") ReferenceError. Keep it up here.
  let securedCache: boolean | null = null;

  // 1. Offline-first: apply whatever we cached last time, instantly.
  const cached = readCache(LS_KEY);
  if (cached) applyManifest(cached);

  // 2. Revalidate from the network (always — the cache may be stale).
  void revalidate();

  // 3. Live reconcile via the tracker's x-hm-rev piggyback.
  window.addEventListener("hm:rev", (e: Event) => {
    const rev = Number((e as CustomEvent).detail);
    if (Number.isFinite(rev) && rev !== appliedRev) void revalidate();
  });

  // 4. Re-evaluate module scoping on SPA route changes (invalidate the
  //    secured-screen cache first — the path backstop depends on location).
  hookHistory(() => {
    securedCache = null;
    evaluate();
  });

  // 5. Re-evaluate when the DOM changes in a way that flips the "secured
  //    screen" verdict — a login modal opening, a password field rendering
  //    in an SPA, etc. Debounced so a busy page doesn't thrash. Only watches
  //    for added/removed nodes; the password-field scan itself is cheap.
  watchSecuredScreen(evaluate);

  async function revalidate(): Promise<void> {
    const m = await fetchManifest();
    if (!m) return;
    writeCache(LS_KEY, m);
    applyManifest(m);
  }

  function applyManifest(m: Manifest): void {
    appliedRev = m.rev;
    current = m;
    evaluate();
  }

  // Decide, for the CURRENT path, which modules should be mounted — then mount
  // the ones that should be and aren't, and unmount the ones that shouldn't be.
  function evaluate(): void {
    if (!current) return;
    const wanted = new Set(current.mods.map((x) => x.k));

    // Modules dropped from the manifest entirely (disabled server-side).
    for (const k of Array.from(injected)) {
      if (!wanted.has(k)) unmountModule(k, findMod(k));
    }

    const secured = isSecuredScreen();
    for (const mod of current.mods) {
      // A module runs if its path matches AND (it's not skipOnAuth OR the
      // current screen isn't a secured/login one). The accessibility widget
      // defaults to skipOnAuth so it never floats over a login box.
      const run =
        pathMatches(location.pathname, mod.cfg?.match) &&
        !(mod.cfg?.skipOnAuth && secured);
      if (run) {
        if (injected.has(mod.k) || pendingIdle.has(mod.k)) continue;
        if (mod.when === "head") {
          injectModule(mod);
        } else {
          pendingIdle.add(mod.k);
          onIdle(() => {
            pendingIdle.delete(mod.k);
            // Re-check: the path may have changed while we waited.
            if (!injected.has(mod.k) && pathMatches(location.pathname, mod.cfg?.match)) {
              injectModule(mod);
            }
          });
        }
      } else if (injected.has(mod.k)) {
        unmountModule(mod.k, mod);
      }
    }
  }

  function findMod(k: string): ModuleEntry | undefined {
    return current?.mods.find((m) => m.k === k);
  }

  function injectModule(mod: ModuleEntry): void {
    if (injected.has(mod.k)) return;
    injected.add(mod.k);
    const s = document.createElement("script");
    s.src = mod.s;
    s.async = true;
    if (mod.d) s.dataset.site = mod.d;
    if (mod.a) s.dataset.api = mod.a;
    // Full console capture is a per-site module config flag; the tracker
    // reads it off its own tag (it never sees the manifest itself).
    if (mod.cfg?.console === true || mod.cfg?.console === 1 || mod.cfg?.console === "1") {
      s.dataset.console = "1";
    }
    // CMP gate (R2): when the hotmaps module carries consentGate, the tracker
    // must not start recording until the consent module grants "statistics".
    if (mod.cfg?.consentGate === true || mod.cfg?.consentGate === 1 || mod.cfg?.consentGate === "1") {
      s.dataset.consent = "1";
    }
    // The consent module gets its full banner config as JSON on its tag —
    // small (texts/colors/survey chips), and keeps the module self-contained.
    if (mod.k === "consent" && mod.cfg) {
      try { s.dataset.hmCfg = JSON.stringify(mod.cfg); } catch { /* huge/circular — skip */ }
    }
    s.dataset.hmModule = mod.k;
    const host =
      mod.when === "idle"
        ? document.body || document.documentElement
        : document.head || document.documentElement;
    host.appendChild(s);
  }

  // Remove the injected <script>, fire an unmount event the module can honor,
  // and remove the module's known host nodes (cfg.hostSelectors) so its UI
  // actually disappears — e.g. the NAGISOT FAB when entering an admin area.
  function unmountModule(k: string, mod: ModuleEntry | undefined): void {
    injected.delete(k);
    try {
      window.dispatchEvent(new CustomEvent("hm:unmount:" + k));
    } catch {
      /* old browsers */
    }
    document.querySelectorAll(`script[data-hm-module="${cssEscapeAttr(k)}"]`).forEach((n) => n.remove());
    for (const sel of mod?.cfg?.hostSelectors ?? []) {
      try {
        document.querySelectorAll(sel).forEach((n) => n.remove());
      } catch {
        /* bad selector — ignore */
      }
    }
  }

  async function fetchManifest(): Promise<Manifest | null> {
    try {
      const productsQ = products ? `products=${encodeURIComponent(products)}` : "";
      const url =
        ident!.kind === "id"
          ? `${manifestOrigin}/m/${encodeURIComponent(ident!.val)}${productsQ ? "?" + productsQ : ""}`
          : `${manifestOrigin}/m?host=${encodeURIComponent(ident!.val)}${productsQ ? "&" + productsQ : ""}`;
      const r = await fetch(url, {
        credentials: "omit",
        mode: "cors",
      });
      if (!r.ok) return null;
      const j = (await r.json()) as Manifest;
      if (!j || !Array.isArray(j.mods)) return null;
      return j;
    } catch {
      return null; // offline / blocked — keep whatever we already applied
    }
  }

  // --- path matching --------------------------------------------------------
  // Glob: '*' matches any run of chars within a segment span; patterns are
  // anchored to the full pathname. A bare prefix like "/admin" also matches
  // "/admin/anything" (treated as "/admin" or "/admin/*").
  function pathMatches(pathname: string, match: MatchRule | undefined): boolean {
    if (!match) return true;
    const p = pathname || "/";
    if (match.exclude && match.exclude.some((g) => globMatch(p, g))) return false;
    if (match.include && match.include.length) return match.include.some((g) => globMatch(p, g));
    return true;
  }
  function globMatch(path: string, pattern: string): boolean {
    if (!pattern) return false;
    const exact = globToRegExp(pattern);
    if (exact.test(path)) return true;
    // Prefix convenience: "/admin" also covers "/admin/*".
    if (!pattern.includes("*") && globToRegExp(pattern.replace(/\/$/, "") + "/*").test(path)) {
      return true;
    }
    return false;
  }
  function globToRegExp(glob: string): RegExp {
    const re = glob
      .replace(/[.+^${}()|[\]\\]/g, "\\$&") // escape regex specials (keep *)
      .replace(/\*/g, ".*");
    return new RegExp("^" + re + "$");
  }

  // --- secured-screen detection ---------------------------------------------
  // "Secured" = the visitor is on a login / sign-up / payment / account-auth
  // screen. The strongest zero-config signal is a VISIBLE password field; we
  // also treat a small set of obvious auth path segments as a backstop so a
  // password-less magic-link login still counts. An author can force a field
  // to be ignored with data-hm-ignore on the input.
  // (securedCache is declared up in the boot-state block above — it is read
  //  during the synchronous applyManifest(cached) path, so it must be
  //  initialized before the boot sequence runs. See the note there.)
  function isSecuredScreen(): boolean {
    if (securedCache !== null) return securedCache;
    securedCache = computeSecured();
    return securedCache;
  }
  function computeSecured(): boolean {
    try {
      const pw = document.querySelector<HTMLInputElement>(
        'input[type="password"]:not([data-hm-ignore])'
      );
      if (pw && isVisible(pw)) return true;
    } catch {
      /* querySelector can't really throw here, but be safe */
    }
    // Path backstop for password-less auth (magic link, OTP, SSO callbacks).
    const p = (location.pathname || "/").toLowerCase();
    return /(^|\/)(login|signin|sign-in|sign_in|log-in|auth|sso|checkout|payment|account\/(login|security))(\/|$)/.test(p);
  }
  function isVisible(el: HTMLElement): boolean {
    if (!el.offsetParent && el.offsetWidth === 0 && el.offsetHeight === 0) return false;
    const cs = getComputedStyle(el);
    return cs.visibility !== "hidden" && cs.display !== "none";
  }
  // Re-evaluate on DOM mutations, debounced. We invalidate the cache then run
  // the caller's evaluate() so modules mount/unmount as the auth state flips.
  function watchSecuredScreen(onChange: () => void): void {
    if (typeof MutationObserver === "undefined") return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (t) return;
      t = setTimeout(() => {
        t = undefined;
        const prev = securedCache;
        securedCache = null; // invalidate
        if (computeSecured() !== prev) onChange();
      }, 300);
    };
    const start = () => {
      try {
        new MutationObserver(schedule).observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      } catch {
        /* ignore */
      }
    };
    if (document.body) start();
    else document.addEventListener("DOMContentLoaded", start, { once: true });
  }

  // --- helpers --------------------------------------------------------------
  function resolveSiteId(el: HTMLScriptElement | null): string | undefined {
    const ok = (v: string | undefined): v is string => !!v && /^[A-Za-z0-9_-]{6,40}$/.test(v);
    if (ok(el?.dataset.site)) return el!.dataset.site;
    try {
      const tags = document.querySelectorAll<HTMLScriptElement>("script[data-site]");
      for (let i = 0; i < tags.length; i++) {
        const v = tags[i]?.dataset.site;
        if (ok(v)) return v;
      }
    } catch {
      /* defensive */
    }
    return undefined;
  }

  function onIdle(fn: () => void): void {
    const run = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
        .requestIdleCallback;
      if (ric) ric(fn);
      else setTimeout(fn, 1);
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run, { once: true });
    } else {
      run();
    }
  }

  // Re-run `fn` whenever the SPA changes route. Patches history methods once.
  function hookHistory(fn: () => void): void {
    const fire = () => {
      try {
        fn();
      } catch {
        /* never break the host page */
      }
    };
    try {
      const op = history.pushState;
      const or = history.replaceState;
      history.pushState = function (...a: Parameters<typeof op>) {
        const r = op.apply(this, a);
        fire();
        return r;
      };
      history.replaceState = function (...a: Parameters<typeof or>) {
        const r = or.apply(this, a);
        fire();
        return r;
      };
      window.addEventListener("popstate", fire);
      window.addEventListener("hashchange", fire);
    } catch {
      /* history unavailable — initial evaluate still ran */
    }
  }

  function readCache(key: string): Manifest | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const m = JSON.parse(raw) as Manifest;
      return m && Array.isArray(m.mods) ? m : null;
    } catch {
      return null;
    }
  }
  function writeCache(key: string, m: Manifest): void {
    try {
      localStorage.setItem(key, JSON.stringify(m));
    } catch {
      /* DNT / incognito / full — fine, we just refetch next time */
    }
  }

  function cssEscapeAttr(s: string): string {
    return s.replace(/["\\]/g, "\\$&");
  }

  void __LOADER_VERSION__;
})();
